import React, { useState } from 'react';
import { useOrg } from '../../../hooks/useOrg';
import { useLocations } from '../../../hooks/useLocations';
import { api } from '../../../lib/api';
import LocationFormModal from '../../locations/LocationFormModal';
import LocationImportModal from '../../locations/LocationImportModal';
import { MapPin, Plus, Star, Pencil, Trash2, Phone, Upload } from 'lucide-react';

export default function LocationsTab() {
  const { currentOrg, currentRole } = useOrg();
  const { locations, refetchLocations } = useLocations();
  const [editing, setEditing] = useState(null); // 'new' | location object | null
  const [importing, setImporting] = useState(false);

  const isAdmin = !!currentRole && currentRole !== 'client';

  const handlePromote = async (loc) => {
    if (loc.is_primary) return;
    try {
      await api.updateLocation(currentOrg.id, loc.id, { is_primary: true });
      await refetchLocations();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleArchive = async (loc) => {
    if (loc.is_primary) {
      alert('Cannot remove the primary location. Promote another location first.');
      return;
    }
    if (!window.confirm(`Remove "${loc.name}"? Existing appointments stay assigned to this location until you reassign them.`)) {
      return;
    }
    try {
      await api.deleteLocation(currentOrg.id, loc.id);
      await refetchLocations();
    } catch (err) {
      alert(err.message);
    }
  };

  if (!currentOrg) return null;

  return (
    <div className="space-y-4">
      <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-[16px] font-bold text-primary-c">Locations</h2>
            <p className="text-[12px] text-muted-c mt-1">
              {currentOrg?.type === 'medical_practice'
                ? 'Each location is billed at the same rate as your base plan. Switch between locations from the header dropdown.'
                : 'Add each of your offices. Switch between locations from the header dropdown.'}
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
            <button
              onClick={() => setImporting(true)}
              className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5"
            >
              <Upload size={14} strokeWidth={2.5} />
              Import CSV
            </button>
            <button
              onClick={() => setEditing('new')}
              className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
            >
              <Plus size={14} strokeWidth={2.5} />
              Add location
            </button>
            </div>
          )}
        </div>

        {locations.length === 0 ? (
          <div className="text-center py-10 text-[13px] text-muted-c">
            No locations yet. Add your first location to get started.
          </div>
        ) : (
          <div className="space-y-2.5">
            {locations.map(loc => (
              <div
                key={loc.id}
                className="flex items-start justify-between gap-4 p-4 rounded-[12px] border border-theme bg-secondary hover:border-accent-light/40 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-[10px] bg-accent-glow flex items-center justify-center text-accent-light flex-shrink-0">
                    <MapPin size={16} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-[14px] font-semibold text-primary-c truncate">{loc.name}</h3>
                      {loc.is_primary && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-glow text-accent-light flex items-center gap-1">
                          <Star size={9} strokeWidth={2.5} fill="currentColor" /> Primary
                        </span>
                      )}
                      {currentOrg?.type === 'medical_practice' && locations.length > 1 && (
                        loc.billing_mode === 'free_until_referral' ? (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${loc.billing_starts_at ? 'bg-warning-soft text-warning' : 'bg-tertiary text-muted-c'}`} title="Joins your subscription 24 hours after a referred patient's first visit here is confirmed">
                            {loc.billing_starts_at ? 'Paid soon — referral booked' : 'Free until it receives a case'}
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-success-soft text-success">Paid</span>
                        )
                      )}
                    </div>
                    <div className="text-[12px] text-muted-c mt-0.5 space-y-0.5">
                      {(loc.address_line1 || loc.city) && (
                        <div className="truncate">
                          {[loc.address_line1, loc.address_line2, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                        </div>
                      )}
                      {loc.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={11} strokeWidth={2.2} />
                          {loc.phone}
                        </div>
                      )}
                      <div className="text-[11px] text-muted-c/80">{loc.timezone}</div>
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!loc.is_primary && (
                      <button
                        onClick={() => handlePromote(loc)}
                        className="px-2.5 py-1.5 rounded-[8px] text-[11px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                        title="Set as primary"
                      >
                        Set primary
                      </button>
                    )}
                    <button
                      onClick={() => setEditing(loc)}
                      className="w-8 h-8 rounded-[8px] flex items-center justify-center text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                      title="Edit"
                    >
                      <Pencil size={13} strokeWidth={2.2} />
                    </button>
                    {!loc.is_primary && (
                      <button
                        onClick={() => handleArchive(loc)}
                        className="w-8 h-8 rounded-[8px] flex items-center justify-center text-danger bg-tertiary border border-theme hover:bg-danger-soft transition-all"
                        title="Remove"
                      >
                        <Trash2 size={13} strokeWidth={2.2} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <LocationFormModal editing={editing} onClose={() => setEditing(null)} />
      {importing && <LocationImportModal onClose={() => setImporting(false)} />}
    </div>
  );
}
